<template>
  <loading-wrapper :is-loading="!file && !digitalizationDocument">
    <div id="document-detail-view">
      <div v-if="file || digitalizationDocument" id="document-detail-view">
        <div id="title-panel">
          <h2>{{ $t('documentDetailView.document') }}</h2>
          <h1>{{ documentName }}</h1>
        </div>

        <PVTabs v-model:value="activeTab" lazy>
          <PVTabsList>
            <router-link :to="getTabLink(DocumentDetailViewTab.DOCUMENT)">
              <PVTab :value="DocumentDetailViewTab.DOCUMENT">
                {{ $t('documentDetailView.tabs.document') }}
              </PVTab>
            </router-link>
            <router-link
              :to="getTabLink(DocumentDetailViewTab.DOCUMENT_HISTORY)"
            >
              <PVTab :value="DocumentDetailViewTab.DOCUMENT_HISTORY">
                {{ $t('documentDetailView.tabs.history') }}
              </PVTab>
            </router-link>
          </PVTabsList>
          <PVTabPanels>
            <PVTabPanel :value="DocumentDetailViewTab.DOCUMENT">
              <div id="document-tab">
                <document-status-indicator
                  v-if="file || digitalizationDocument"
                  class="document-status-indicator"
                  :mode="mode"
                  :file="file"
                  :digitalization-document="digitalizationDocument"
                  @document-data-changed="getFile"
                ></document-status-indicator>
                <document-and-metadata
                  v-if="file || digitalizationDocument"
                  :mode="mode"
                  :project-id="
                    file?.projectId.toString()! ||
                    digitalizationDocument?.digitalizationProjectId.toString()!
                  "
                  :file="file"
                  :digitalization-document="digitalizationDocument"
                  :metadata="metadata"
                  :upload-id="
                    file?.uploadId.toString()! ||
                    digitalizationDocument?.uploadId.toString()!
                  "
                  :document-id="
                    file?.documentId.toString()! ||
                    digitalizationDocument?.documentId.toString()!
                  "
                  @document-data-changed="getFile"
                />

                <check-bot-results-table
                  v-if="file || digitalizationDocument"
                  :check-bot-results="
                    (file ?? digitalizationDocument)?.checkBotResults
                  "
                />

                <metadata-check-results-table
                  v-if="file || digitalizationDocument"
                  :metadata-check-results="
                    (file ?? digitalizationDocument)?.metadataCheckResults
                  "
                ></metadata-check-results-table>
              </div>
            </PVTabPanel>
            <PVTabPanel :value="DocumentDetailViewTab.DOCUMENT_HISTORY">
              <document-history
                :mode="mode"
                :upload-id="
                  file?.uploadId.toString() ||
                  digitalizationDocument?.uploadId.toString()!
                "
                :document-id="
                  file?.documentId.toString() ||
                  digitalizationDocument?.documentId.toString()!
                "
              />
            </PVTabPanel>
          </PVTabPanels>
        </PVTabs>
      </div>
    </div>
  </loading-wrapper>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { RouteLocationRaw, useRoute } from 'vue-router';
  import {
    ProjectType,
    ISupplierDocumentDetails,
    IDigitalizationDocumentDetails,
  } from 'types';
  import CheckBotResultsTable from '@/components/CheckBotResultsTable/CheckBotResultsTable.vue';
  import DocumentAndMetadata from '@/components/DocumentAndMetadata/DocumentAndMetadata.vue';
  import DocumentHistory from '@/components/DocumentHistory/DocumentHistory.vue';
  import MetadataCheckResultsTable from '@/components/MetadataCheckResultsTable/MetadataCheckResultsTable.vue';
  import DocumentStatusIndicator from '@/components/DocumentStatusIndicator/DocumentStatusIndicator.vue';
  import endpointsList from '@/helpers/endpointsList';
  import axios from 'axios';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';
  import {
    DocumentDetailViewTab,
    IMetadataEntry,
  } from './DocumentDetailView.types';
  import { useTabs } from '@/composables/useTabs';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const props = defineProps<{
    mode: ProjectType;
    documentOid: string;
  }>();

  const file = ref<ISupplierDocumentDetails | undefined>(undefined);
  const digitalizationDocument = ref<
    IDigitalizationDocumentDetails | undefined
  >(undefined);

  const metadata = computed(() => {
    const metadata: IMetadataEntry[] = [];

    if (props.mode === ProjectType.SUPPLIER_PROJECT && file.value) {
      for (const field in file.value.metadata) {
        metadata.push({
          field,
          fieldDisplayName: file.value.metadata[field].fieldDisplayName,
          value: file.value.metadata[field],
        });
      }
    } else if (
      props.mode === ProjectType.DIGITALIZATION_PROJECT &&
      digitalizationDocument.value
    ) {
      for (const field in digitalizationDocument.value.fileMetadata) {
        metadata.push({
          field,
          fieldDisplayName:
            digitalizationDocument.value.fileMetadata[field].fieldDisplayName,
          value: digitalizationDocument.value.fileMetadata[field],
        });
      }
    }

    return metadata;
  });

  const documentName = computed(() => {
    const nameField = metadata.value.find(
      (f: { field: string; value: any }) => f.field === 'Name',
    );

    return nameField?.value as string;
  });

  async function getFile() {
    try {
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        const response = await axios.get(
          endpointsList.documentsUploads.getFile(props.documentOid),
        );
        file.value = response.data;
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        const response = await axios.get(
          endpointsList.digitalizationUploads.getDigitalizationDocumentData(
            props.documentOid,
          ),
        );
        digitalizationDocument.value = response.data;
      }
    } catch {
      createErrorToast(t('errors.error'));
    }
  }

  const { activeTab } = useTabs(
    Route.DOCUMENT_DETAIL_VIEW,
    DocumentDetailViewTab,
    DocumentDetailViewTab.DOCUMENT,
  );
  const route = useRoute();
  function getTabLink(tab: DocumentDetailViewTab): RouteLocationRaw {
    return {
      name: Route.DOCUMENT_DETAIL_VIEW,
      params: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW][PROJECT_TYPE_PARAM]]:
          props.mode,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].documentOid]:
          props.documentOid,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].tab]: tab,
      },
      query: route.query,
    };
  }

  onMounted(() => {
    getFile();
  });
</script>

<style scoped lang="scss">
  #document-detail-view {
    #title-panel {
      margin-bottom: 50px;

      h2 {
        font-size: $subheading-font-size;
      }

      h1 {
        font-size: 50px;
      }
    }

    @include tabs-list;

    #document-tab {
      margin-top: 30px;

      .document-status-indicator {
        margin-bottom: 3rem;
      }
    }

    #metadata-check-results-table {
      margin-top: 50px;
    }
  }
</style>
